(function initializeMembershipPricing() {
  if (window.WYWMembershipPricingLoaded) return;
  window.WYWMembershipPricingLoaded = true;

  class MembershipPricing extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.buttons = [...this.querySelectorAll('[data-period-button]')];
      this.status = this.querySelector('[data-period-status]');
      this.monthlyOnlyCards = [...this.querySelectorAll('[data-monthly-only="true"]')];
      this.buttons.forEach((button) => button.addEventListener('click', () => this.selectPeriod(button.dataset.periodButton)));
      this.initializeCheckoutForms();
      this.initializeStickyCta();
    }

    initializeCheckoutForms() {
      this.forms = [...this.querySelectorAll('form')];
      this.cartError = this.querySelector('[data-membership-cart-error]');
      this.hideRebuySmartCart = () => window.Rebuy?.SmartCart?.hide?.();
      this.forms.forEach((form) => {
        form.addEventListener('submit', (event) => this.replaceCartAndSubmit(event, form));
      });
    }

    beginSmartCartSuppression() {
      document.addEventListener('rebuy:smartcart.show', this.hideRebuySmartCart);
      this.hideRebuySmartCart();
    }

    endSmartCartSuppression() {
      document.removeEventListener('rebuy:smartcart.show', this.hideRebuySmartCart);
    }

    async replaceCartAndSubmit(event, form) {
      event.preventDefault();
      if (this.checkoutPending) return;

      this.checkoutPending = true;
      this.beginSmartCartSuppression();
      if (this.cartError) this.cartError.hidden = true;
      const submitButtons = this.forms.map((candidate) => candidate.querySelector('[type="submit"]')).filter(Boolean);
      submitButtons.forEach((button) => { button.disabled = true; });

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/clear.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Cart clear failed with status ${response.status}`);

        this.hideRebuySmartCart();
        HTMLFormElement.prototype.submit.call(form);
      } catch (error) {
        console.error('Unable to prepare membership checkout.', error);
        this.checkoutPending = false;
        this.endSmartCartSuppression();
        submitButtons.forEach((button) => { button.disabled = false; });
        if (this.cartError) {
          this.cartError.textContent = 'We couldn\'t prepare your membership checkout. Please try again.';
          this.cartError.hidden = false;
        }
      }
    }

    selectPeriod(period) {
      this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.periodButton === period)));
      this.querySelectorAll('[data-tier-card]').forEach((card) => {
        const effectivePeriod = card.dataset.monthlyOnly === 'true' ? 'monthly' : period;
        card.querySelectorAll('[data-period-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.periodPanel !== effectivePeriod;
        });
      });
      if (this.status) this.status.textContent = period === 'annual' ? this.dataset.annualStatus : this.dataset.monthlyStatus;
    }

    initializeStickyCta() {
      this.stickyCta = this.querySelector('[data-membership-sticky-cta]');
      if (!this.stickyCta) return;

      this.stickyLink = this.querySelector('[data-membership-sticky-link]');
      // Default to "pricing visible" so browsers without IntersectionObserver never show the bar.
      this.pricingVisible = true;
      this.footerVisible = false;
      // Keep the bar off the landing view: it may only appear once the visitor has
      // scrolled the first screen (the fold) away, never above it at the top of the page.
      this.scrolledPastFold = false;
      this.stickyLink?.addEventListener('click', (event) => {
        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        window.setTimeout(() => this.querySelector('h2')?.focus({ preventScroll: true }), reduceMotion ? 0 : 450);
      });

      if (!('IntersectionObserver' in window)) {
        this.updateStickyCta();
        return;
      }

      this.pricingObserver = new IntersectionObserver(([entry]) => {
        // Show the sticky CTA whenever the pricing section is out of view — above or below.
        this.pricingVisible = entry.isIntersecting;
        this.updateStickyCta();
      }, { threshold: 0 });
      this.pricingObserver.observe(this);

      const footer = document.querySelector('footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.footerVisible = entry.isIntersecting;
          this.updateStickyCta();
        });
        this.footerObserver.observe(footer);
      }

      this.onStickyScroll = () => {
        const scrolledPastFold = window.scrollY > window.innerHeight;
        if (scrolledPastFold !== this.scrolledPastFold) {
          this.scrolledPastFold = scrolledPastFold;
          this.updateStickyCta();
        }
      };
      window.addEventListener('scroll', this.onStickyScroll, { passive: true });
      this.onStickyScroll();
    }

    updateStickyCta() {
      if (!this.stickyCta) return;
      const visible = this.scrolledPastFold && !this.pricingVisible && !this.footerVisible;
      this.stickyCta.hidden = !visible;
      this.stickyCta.setAttribute('aria-hidden', String(!visible));
      document.documentElement.classList.toggle('membership-sticky-cta-visible', visible);
      if (visible) {
        window.requestAnimationFrame(() => {
          document.documentElement.style.setProperty('--membership-sticky-cta-height', `${this.stickyCta.offsetHeight}px`);
        });
      }
    }

    disconnectedCallback() {
      this.endSmartCartSuppression();
      this.pricingObserver?.disconnect();
      this.footerObserver?.disconnect();
      if (this.onStickyScroll) window.removeEventListener('scroll', this.onStickyScroll);
      document.documentElement.classList.remove('membership-sticky-cta-visible');
      document.documentElement.style.removeProperty('--membership-sticky-cta-height');
    }
  }

  if (!customElements.get('membership-pricing')) customElements.define('membership-pricing', MembershipPricing);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('membership-pricing').forEach((element) => element.connectedCallback());
  });
})();
